// 薬局 情報伝達ハブ — 共有型定義
// src/ と functions/ の双方から参照する(ランタイム依存なしの純粋な型のみ)

export const CATEGORIES = [
  "注意喚起・安全情報",
  "服薬指導・薬品情報",
  "在庫・発注連絡",
  "行政・制度情報",
  "一般連絡・お知らせ",
] as const;
export type Category = (typeof CATEGORIES)[number];

export const URGENCIES = ["高", "中", "低"] as const;
export type Urgency = (typeof URGENCIES)[number];

export type Scope = "common" | "targeted";

export interface Company {
  id: string; // = companyId (URLに使う。英数小文字+ハイフン)
  name: string;
  equipment: string;
  branding?: { color?: string; logoUrl?: string };
  active: boolean;
  lineConfigured: boolean; // ★lineChannelAccessToken本体はクライアントへ返さない。真偽値のみ
  createdAt?: string;
  updatedAt?: string;
}

// サーバー(functions/api)内部でのみ扱う。LINEチャネルアクセストークンを含む完全版
export interface CompanyInternal extends Omit<Company, "lineConfigured"> {
  lineChannelAccessToken: string; // LINE公式アカウントのMessaging APIチャネルアクセストークン
}

export interface ClassifiedFields {
  title: string;
  category: Category;
  urgency: Urgency;
  date: string; // YYYY-MM-DD
  summary: string;
  points: string[];
  actions: string[];
  target: string;
  deadline: string;
  contact: string;
}

export interface Material extends ClassifiedFields {
  id: string;
  body: string;
  org: string;
  sourcePdfName?: string;
  sourcePdfPath?: string;
  rawText?: string;
  scope: Scope;
  companyIds: string[];
  published: boolean;
  publishedAt?: string; // 初回配信日時。これが無ければ「まだ一度もLINE送信していない」
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type SendChannel = "line" | "portal";
export type SendStatus = "ok" | "error";

export interface SendLog {
  id: string;
  materialId: string;
  companyId: string;
  companyName: string;
  channel: SendChannel;
  status: SendStatus;
  httpStatus?: number;
  error?: string; // 例: 'no_line_token'
  sentBy: string;
  sentAt: string;
}

export interface UserRecord {
  uid: string;
  email: string;
  companyId: string | null; // role=hq は null 可
  role: "hq" | "staff";
}

// classify APIのリクエスト/レスポンス
export interface ClassifyResponse extends ClassifiedFields {
  fallback: boolean; // true: LLM失敗によりルールベースへフォールバックした
}

export const RESERVED_COMPANY_IDS = new Set([
  "c",
  "api",
  "hq",
  "admin",
  "portal",
  "assets",
  "static",
  "functions",
  "login",
  "logout",
  "www",
  "app",
  "public",
  "index",
  "favicon.ico",
]);

const COMPANY_ID_RE = /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/;

export function isValidCompanyId(id: string): boolean {
  return (
    typeof id === "string" &&
    id.length >= 2 &&
    id.length <= 40 &&
    COMPANY_ID_RE.test(id) &&
    !RESERVED_COMPANY_IDS.has(id)
  );
}
